<template>
  <main class="d-flex flex-column align-center justify-center">
    <h1>Release bundle comparator</h1>

    <div class="d-flex flex-row justify-space-between">
      <div>
        <v-select
          v-model="selectedReleaseId1"
          :items="releaseBundlesMinimal"
          item-value="id"
          item-text="version"
          label="Release"
        >
          <template v-slot:selection="{ item }">
            <span>
              {{ `${removePatchVersion(item.version)} (${item.name})` }}
            </span>
          </template>

          <template v-slot:item="{ item }">
            <span>
              {{ `${removePatchVersion(item.version)} (${item.name})` }}
            </span>
          </template>
        </v-select>
        <v-select
          v-model="selectedBuildId1"
          :items="buildsForRelease1"
          item-value="id"
          item-text="version"
          label="Build"
        >
          <template v-slot:selection="{ item }">
            <span>
              {{ `${item.version} (Build ${item.buildNumber})` }}
            </span>
          </template>

          <template v-slot:item="{ item }">
            <span>
              {{ `${item.version} (Build ${item.buildNumber})` }}
            </span>
          </template>
        </v-select>
      </div>
      <v-icon class="mx-6">mdi-arrow-left-right</v-icon>
      <div>
        <v-select
          v-model="selectedReleaseId2"
          :items="releaseBundlesMinimal"
          item-value="id"
          item-text="version"
          label="Release"
        >
          <template v-slot:selection="{ item }">
            <span>
              {{ `${removePatchVersion(item.version)} (${item.name})` }}
            </span>
          </template>

          <template v-slot:item="{ item }">
            <span>
              {{ `${removePatchVersion(item.version)} (${item.name})` }}
            </span>
          </template>
        </v-select>
        <v-select
          v-model="selectedBuildId2"
          :items="buildsForRelease2"
          item-value="id"
          item-text="version"
          label="Build"
        >
          <template v-slot:selection="{ item }">
            <span>
              {{ `${item.version} (Build ${item.buildNumber})` }}
            </span>
          </template>

          <template v-slot:item="{ item }">
            <span>
              {{ `${item.version} (Build ${item.buildNumber})` }}
            </span>
          </template>
        </v-select>
      </div>
    </div>

    <v-divider class="my-4" />

    <v-card v-if="build1 && build2" outlined>
      <v-card-title class="d-flex justify-center">
        <div>Build comparison</div>
      </v-card-title>

      <v-card-text>
        <div class="d-flex align-center mb-4">
          <v-switch
            v-model="onlyDiffs"
            inset
            label="Show only differences"
            class="mr-4"
          />
        </div>

        <v-simple-table>
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">{{ leftLabel }}</th>
              <th class="text-left">{{ rightLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayBaseRows" :key="row.key">
              <td class="font-weight-medium">{{ row.label }}</td>
              <td :class="row.isDiff ? 'diff-cell' : ''">{{ row.left }}</td>
              <td :class="row.isDiff ? 'diff-cell' : ''">{{ row.right }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Packages -->
        <div class="mt-6 mb-2 subtitle-2">Packages</div>
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">Package</th>
              <th class="text-left">{{ leftLabel }}</th>
              <th class="text-left">{{ rightLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayPackageRows" :key="row.key">
              <td class="font-weight-medium">{{ row.label }}</td>
              <td :class="row.isDiff ? 'diff-cell' : ''">{{ row.left }}</td>
              <td :class="row.isDiff ? 'diff-cell' : ''">{{ row.right }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </main>
</template>

<script>
import { removePatchVersion } from '../../utils/releaseUtils';
import {
  GET_RELEASE_BUNDLE_BUILD,
  GET_ALL_RELEASE_BUNDLES_MINIMAL,
  GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
} from '../../graphql/upgradePackageQueries';

export default {
  name: 'ReleaseBundleComparator',
  props: {},
  data() {
    return {
      onlyDiffs: false,
      selectedReleaseId1: null,
      selectedReleaseId2: null,
      selectedBuildId1: null,
      selectedBuildId2: null,
      build1: null,
      build2: null,
    };
  },
  apollo: {
    $client: 'upgradePackageClient',
    releaseBundlesMinimal: {
      query: GET_ALL_RELEASE_BUNDLES_MINIMAL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          includeInfo: false,
        };
      },
      update: (data) => data.getAllReleaseBundles,
    },
    buildsForRelease1: {
      query: GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          releaseBundleId: this.selectedReleaseId1,
        };
      },
      update: (data) => data.getAllReleaseBundleBuilds,
      skip() {
        return !this.selectedReleaseId1;
      },
    },
    buildsForRelease2: {
      query: GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          releaseBundleId: this.selectedReleaseId2,
        };
      },
      update: (data) => data.getAllReleaseBundleBuilds,
      skip() {
        return !this.selectedReleaseId2;
      },
    },
    build1: {
      query: GET_RELEASE_BUNDLE_BUILD,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          releaseBundleBuildId: this.selectedBuildId1,
        };
      },
      update: (data) => data.getReleaseBundleBuild,
      skip() {
        return !this.selectedBuildId1;
      },
    },
    build2: {
      query: GET_RELEASE_BUNDLE_BUILD,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          releaseBundleBuildId: this.selectedBuildId2,
        };
      },
      update: (data) => data.getReleaseBundleBuild,
      skip() {
        return !this.selectedBuildId2;
      },
    },
  },
  methods: {
    mkRow(key, label, left, right) {
      return {
        key,
        label,
        left: this.display(left),
        right: this.display(right),
        isDiff: this.normalize(left) !== this.normalize(right),
      };
    },

    // Helpers
    display(v) {
      if (!v) return '—';
      if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
      return String(v);
    },
    normalize(v) {
      if (!v) return '';
      if (Array.isArray(v))
        return v.map((x) => String(x).trim().toLowerCase()).join(',');
      return String(v).trim().toLowerCase();
    },
    formatDate(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return String(iso);
      }
    },
    removePatchVersion,
  },
  computed: {
    leftLabel() {
      if (!this.build1) return '—';
      return `${this.build1.version} (Build ${this.build1.buildNumber})`;
    },
    rightLabel() {
      if (!this.build2) return '—';
      return `${this.build2.version} (Build ${this.build2.buildNumber})`;
    },

    baseRows() {
      const l = this.build1,
        r = this.build2;
      if (!l || !r) return [];

      return [
        this.mkRow('buildNumber', 'Build', l.buildNumber, r.buildNumber),
        this.mkRow('version', 'Version', l.version, r.version),
        this.mkRow(
          'name',
          'Name',
          l.releaseBundle?.name,
          r.releaseBundle?.name
        ),
        this.mkRow(
          'channels',
          'Release channel(s)',
          l.releaseBundle?.releaseChannels ?? [],
          r.releaseBundle?.releaseChannels ?? []
        ),
        this.mkRow(
          'pub',
          'Publication status',
          l.publicationStatus,
          r.publicationStatus
        ),
        this.mkRow(
          'updatedAt',
          'Updated at',
          this.formatDate(l.updatedAt),
          this.formatDate(r.updatedAt)
        ),
      ].filter(Boolean);
    },
    displayBaseRows() {
      return this.onlyDiffs
        ? this.baseRows.filter((x) => x.isDiff)
        : this.baseRows;
    },
    packageRows() {
      const lp = this.build1?.releaseBundle?.packages || [];
      const rp = this.build2?.releaseBundle?.packages || [];

      const lMap = new Map(lp.map((p) => [p.upgradePackageTemplateId, p]));
      const rMap = new Map(rp.map((p) => [p.upgradePackageTemplateId, p]));
      const keys = new Set([...lMap.keys(), ...rMap.keys()]);

      const rows = Array.from(keys).map((key) => {
        const L = lMap.get(key);
        const R = rMap.get(key);
        const label = L?.name || R?.name || key;
        const left = L?.version ?? '—';
        const right = R?.version ?? '—';
        return this.mkRow(`pkg:${key}`, label, left, right);
      });

      // Sort packages by name
      rows.sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
      return rows;
    },
    displayPackageRows() {
      return this.onlyDiffs
        ? this.packageRows.filter((x) => x.isDiff)
        : this.packageRows;
    },
  },
};
</script>

<style scoped>
.diff-cell {
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 6px;
  background-color: rgba(255, 193, 7, 0.18);
}
</style>
